/**
 * CSP domain whitelist panel (GR-3612), rendered inside the web tracking section for both
 * the organisation and campaign template editors.
 *
 * A scrollable list of whitelisted domains with add, edit and remove per row, bound to the
 * editor's data template (template.value.cspDomains): every change is a template-builder
 * write, persisted by the wizard's Save all. The public sites apply org and campaign lists
 * accumulatively, so the campaign editor shows a note when org-level domains exist. Payment
 * (card-entry) pages never receive these domains, which the info box spells out.
 */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Button, Icon, Input, Message } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';

import { TemplateHeader } from '../../template/templateHeader';
import { InfoBox } from '../../common/infoBox';
import { WriteOnly } from '../../common/grid/WriteOnly';
import * as templateHelper from '../../template/templateHelper';
import { isValidCspDomain, normalizeCspDomain } from '../helpers';
import { i18nKeys } from '../constants';
import styles from '../CspDomains.module.css';

interface CspDomainsPanelOwnProps {
  type: 'organisation' | 'campaign';
  path: string;
  onChange: (path: string, value: string[]) => void;
}

interface CspDomainsPanelProps extends CspDomainsPanelOwnProps {
  domains: string[];
  organisationDomains: string[];
}

const CspDomainsPanelView: React.FC<CspDomainsPanelProps> = ({
  type,
  path,
  onChange,
  domains,
  organisationDomains
}) => {
  const [newDomain, setNewDomain] = useState('');
  const [newDomainError, setNewDomainError] = useState(false);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editValueError, setEditValueError] = useState(false);

  const onAdd = () => {
    const domain = normalizeCspDomain(newDomain);
    if (!isValidCspDomain(domain)) {
      setNewDomainError(true);
      return;
    }
    setNewDomainError(false);
    setNewDomain('');
    if (!domains.includes(domain)) {
      onChange(path, [...domains, domain]);
    }
  };

  const onStartEdit = (index: number) => {
    setEditingIndex(index);
    setEditValue(domains[index]);
    setEditValueError(false);
  };

  const onSaveEdit = () => {
    if (editingIndex == null) {
      return;
    }
    const domain = normalizeCspDomain(editValue);
    if (!isValidCspDomain(domain)) {
      setEditValueError(true);
      return;
    }
    setEditValueError(false);
    setEditingIndex(null);
    const next = domains.map((item, index) =>
      index === editingIndex ? domain : item
    );
    onChange(path, next);
  };

  const onRemove = (index: number) => {
    if (editingIndex === index) {
      setEditingIndex(null);
    }
    onChange(
      path,
      domains.filter((_, i) => i !== index)
    );
  };

  return (
    <div className={styles.section}>
      <TemplateHeader as="h3" label={i18nKeys.HEADER} />

      <InfoBox
        header={i18nKeys.INFO_HEADER}
        content={
          type === 'campaign'
            ? i18nKeys.INFO_CONTENT_CAMPAIGN
            : i18nKeys.INFO_CONTENT
        }
      />

      {type === 'campaign' && organisationDomains.length > 0 && (
        <InfoBox
          header={i18nKeys.INHERITED_HEADER}
          content={i18nKeys.INHERITED_CONTENT}
        />
      )}

      <div className={styles.list} data-testid="csp-domain-list">
        {domains.length === 0 && (
          <div className={styles.empty}>{I18n.t(i18nKeys.EMPTY)}</div>
        )}
        {domains.map((domain, index) =>
          editingIndex === index ? (
            <div className={styles.row} key={`${domain}-${index}`}>
              <Input
                className={styles.editInput}
                value={editValue}
                error={editValueError}
                onChange={(_, { value }) => setEditValue(value)}
                aria-label={I18n.t(i18nKeys.INPUT_PLACEHOLDER)}
              />
              <Button primary size="mini" onClick={onSaveEdit}>
                {I18n.t(i18nKeys.SAVE_BUTTON)}
              </Button>
              <Button size="mini" onClick={() => setEditingIndex(null)}>
                {I18n.t(i18nKeys.CANCEL_BUTTON)}
              </Button>
            </div>
          ) : (
            <div className={styles.row} key={`${domain}-${index}`}>
              <span className={styles.domain}>{domain}</span>
              <WriteOnly>
                <span>
                  <Button
                    icon
                    basic
                    size="mini"
                    aria-label={I18n.t(i18nKeys.SAVE_BUTTON)}
                    onClick={() => onStartEdit(index)}
                  >
                    <Icon name="pencil" />
                  </Button>
                  <Button
                    icon
                    basic
                    size="mini"
                    aria-label={I18n.t(i18nKeys.REMOVE_BUTTON)}
                    onClick={() => onRemove(index)}
                  >
                    <Icon name="close" />
                  </Button>
                </span>
              </WriteOnly>
            </div>
          )
        )}
        {editValueError && (
          <Message negative content={I18n.t(i18nKeys.ERROR_INVALID)} />
        )}
      </div>

      <WriteOnly>
        <div className={styles.addRow}>
          <Input
            className={styles.addInput}
            placeholder={I18n.t(i18nKeys.INPUT_PLACEHOLDER)}
            value={newDomain}
            error={newDomainError}
            onChange={(_, { value }) => setNewDomain(value)}
            aria-label={I18n.t(i18nKeys.INPUT_PLACEHOLDER)}
          />
          <Button primary onClick={onAdd}>
            {I18n.t(i18nKeys.ADD_BUTTON)}
          </Button>
        </div>
        {newDomainError && (
          <Message negative content={I18n.t(i18nKeys.ERROR_INVALID)} />
        )}
      </WriteOnly>
    </div>
  );
};

const mapState = (state: any, ownProps: CspDomainsPanelOwnProps) => ({
  domains:
    templateHelper.getValue(state.templateBuilderEx, ownProps.path) || [],
  organisationDomains:
    ownProps.type === 'campaign'
      ? templateHelper.getValue(
          state.templateBuilderEx,
          'organization.template.value.cspDomains'
        ) || []
      : []
});

const CspDomainsPanel = connect(mapState)(CspDomainsPanelView);

export default CspDomainsPanel;
